/**
 * Users — show view on the shared Inspector grammar (coverage §2.5).
 * Header / Overview / Relations / Audit, with the named primary actions:
 *   - **activate / deactivate** → `PATCH /api/users/:id { status }` (real).
 *   - **assign roles** → the `user_roles` join is `[PROPOSED]`; the affordance
 *     renders as a teaser relation, never a hard error.
 *
 * Degrades calmly when `/api/users/:id` (`[P]`) hasn't shipped: instead of a
 * crash, the page shows the labeled `[PROPOSED]` empty teaser.
 */
import * as React from "react";
import {
  useShow,
  useGo,
  useNavigation,
  useUpdate,
  type HttpError,
} from "@refinedev/core";
import { Pencil, Power, PowerOff, Users as UsersIcon } from "@/components/ui/icon";
import type { User } from "@aos/shared";

import { Button } from "@/components/ui/button";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  ShowPageSkeleton,
  FieldGrid,
  Field,
  RelationsBlock,
  AuditBlock,
  TechnicalId,
} from "@/components/inspector";
import { StatusBadge } from "@/components/app";
import { ProposedListPage } from "../_shared/ProposedModule";

export function UserShow() {
  const go = useGo();
  const { editUrl } = useNavigation();
  const { query } = useShow<User, HttpError>({
    resource: "users",
    queryOptions: { retry: false },
    errorNotification: false,
  });
  const { data, isLoading, isError } = query;
  const user = data?.data;
  const { mutate: updateUser, isLoading: updating } = useUpdate();

  if (isLoading) return <ShowPageSkeleton />;
  // Endpoint absent / record missing → calm labeled-empty teaser.
  if (isError || !user) {
    return (
      <ProposedListPage
        title="User"
        description="People and service accounts in the operating core."
        icon={UsersIcon}
        endpoint="/api/users/:id"
        emptyTitle="User detail not available"
        testId="user-proposed"
      >
        <Button variant="outline" onClick={() => go({ to: "/users" })}>
          Back to users
        </Button>
      </ProposedListPage>
    );
  }

  const isActive = user.status === "active";
  const toggleStatus = () =>
    updateUser({
      resource: "users",
      id: user.id,
      values: { status: isActive ? "inactive" : "active" },
      successNotification: {
        type: "success",
        message: isActive ? "User deactivated" : "User activated",
      },
    });

  return (
    <Inspector>
      <InspectorHeader
        title={user.display_name}
        subtitle="A person or service account in the operating core."
        status={user.status}
        primaryAction={
          <Button onClick={toggleStatus} disabled={updating}>
            {isActive ? (
              <>
                <PowerOff className="mr-2 h-4 w-4" /> Deactivate
              </>
            ) : (
              <>
                <Power className="mr-2 h-4 w-4" /> Activate
              </>
            )}
          </Button>
        }
        actions={
          <Button
            variant="outline"
            onClick={() => go({ to: editUrl("users", user.id) })}
          >
            <Pencil className="mr-2 h-4 w-4" /> Edit
          </Button>
        }
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Name">{user.display_name}</Field>
          <Field label="Email">{user.email}</Field>
          <Field label="Status">
            <StatusBadge status={user.status} />
          </Field>
          <Field label="Created">
            {new Date(user.created_at).toLocaleString()}
          </Field>
          <Field label="Updated">
            {new Date(user.updated_at).toLocaleString()}
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Relations">
        <RelationsBlock
          groups={[
            {
              title: "Roles",
              emptyMessage:
                "Role membership available once the user_roles join endpoint ships ([PROPOSED]).",
              items: [],
            },
          ]}
        />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={user.id} />
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock entityType="users" entityId={user.id} />
      </InspectorSection>
    </Inspector>
  );
}

export default UserShow;
